import { useContext, useState } from 'react';
import { createOutcomePresentation } from '../../../../api/portfolio-client/portfolio-client';
import { UserContext } from '../../../../contexts';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { appConfig } from '../../../../helpers/app-config/app-config';

/**
 * Hook for creating a Google Slides presentation for outcomes
 *
 * @param {string[]} outcomeIds - a list of outcome ids
 * @returns Object containing state and functions for presentation creation
 */
const useCreateOutcomePresentation = (outcomeIds: string[]) => {
  const [presentationName, setPresentationName] = useState('');
  const [isCreatingPresentation, setIsCreatingPresentation] = useState(false);
  const organization = useGlobalStore((state) => state.organization);
  const portfolioId = useGlobalStore((state) => state.portfolio?.id);
  const { user } = useContext(UserContext);

  const hasGoogleCreds = user?.has_google_creds;

  /**
   * Create a Google Slides presentation with the given name and outcome IDs
   *
   * @returns Promise that resolves to the URL of the created presentation
   */
  const createPresentation = async (): Promise<string> => {
    if (!presentationName || !organization?.id) {
      throw new Error('Presentation name and organization ID are required');
    }

    setIsCreatingPresentation(true);

    try {
      return await createOutcomePresentation(organization.id, presentationName, portfolioId, outcomeIds);
    } finally {
      setIsCreatingPresentation(false);
    }
  };

  /**
   * Get the Google auth URL for connecting a Google account
   *
   * @returns The URL to redirect to for Google authentication
   * @throws Error if organization ID is not available
   */
  const getGoogleAuthUrl = (): string => {
    const organizationId = organization?.id;
    if (!organizationId) {
      throw new Error('Organization ID is required to connect Google');
    }
    return `${appConfig.VITE_APP_API_URL}/api/openid/google_auth?service=google&organization_id=${encodeURIComponent(organizationId)}`;
  };

  return {
    presentationName,
    setPresentationName,
    isCreatingPresentation,
    hasGoogleCreds,
    createPresentation,
    getGoogleAuthUrl,
  };
};

export { useCreateOutcomePresentation };
